import { FormatAddressOptions, convertAddressCasing } from 'lib-address'

import { Address } from '@/entities'
import { getCountryName } from '@/i18n'

import { getCountryData, CountryCode } from './types'
import { validateAddress } from './validateAddress'

import type { AddressInput } from 'lib-address/dist/types'

export type FormatAddressInput = Address & { name?: string }

export const formatAddress = (address: FormatAddressInput, defaultCountry?: CountryCode, language?: string) => {
    let name: string | undefined = undefined

    if (address.name || address.details1 || address.details2) {
        name = [address.name, address.details1, address.details2].filter((x) => !!x).join('\n')
    }

    try {
        return libAddressFormatAddress(
            {
                country: address.country,
                state: address.state,
                zip: address.zip,
                city: address.city,
                addressLine1: address.addressLine,
                name,
            },
            {
                preserveCase: true,
                appendCountry: address.country !== defaultCountry,
                lang: language,
            }
        )
    } catch (e) {
        // happens ie for empty addresses
        return ''
    }
}

export const formatAddressLine = (address: FormatAddressInput, defaultCountry?: CountryCode, language?: string) => {
    return formatAddress(address, defaultCountry, language).split('\n').join(', ')
}

// this is the same function from lib-address but was overridden here to use our custom validateAddress
const libAddressFormatAddress = (address: AddressInput, opts?: FormatAddressOptions): string => {
    const {
        preserveCase = false,
        useStateISOCode = false,
        appendCountry = false,
        useCountryISOCode = false,
        useLatin = false,
        lang,
    } = opts ?? {}

    // use our custom validateAddress
    validateAddress(address)

    const cpy = { ...address }
    const data = getCountryData(cpy.country)

    const stateData = cpy.state ? data.sub_regions?.find((sr) => sr.key === cpy.state) : null

    const fmt = useLatin ? (data.lfmt ?? data.fmt) : data.fmt
    const upper = data.upper

    if (!useStateISOCode && stateData) {
        cpy.state = [lang && stateData.name[lang], useLatin && stateData.name.latin, stateData.name.default].filter(
            Boolean
        )[0] as string
    }

    const {
        addressLine1 = '',
        addressLine2 = '',
        addressLine3 = '',
        city = '',
        dependentLocality = '',
        name = '',
        organization = '',
        sortingCode = '',
        state = '',
        zip = '',
    } = preserveCase ? cpy : convertAddressCasing(cpy, upper)

    const addressLine = [addressLine1, addressLine2, addressLine3].filter(Boolean).join('\n')

    const nationalFormat = fmt
        .replace(/%N/g, name)
        .replace(/%O/g, organization)
        .replace(/%A/g, addressLine)
        .replace(/%D/g, dependentLocality)
        .replace(/%C/g, city)
        .replace(/%S/g, state)
        .replace(/%Z/g, zip)
        .replace(/%X/g, sortingCode)
        .replace(/(%n)+/g, '\n')
        .trim()

    if (appendCountry) {
        const localizedCountryName = lang ? getCountryName(data.key, lang) : undefined
        const countryName = localizedCountryName || data.name

        return useCountryISOCode ? `${nationalFormat}\n${data.key}` : `${nationalFormat}\n${countryName}`
    }

    return nationalFormat
}
